/*
 * Opens the desktop header dropdowns on hover instead of requiring a click.
 *
 * Dawn builds each top-level item as <header-menu><details><summary>, and a
 * <details> only reveals its children when the `open` attribute is present -
 * there is no CSS-only way to open one, so this sets the attribute directly
 * rather than fighting the UA stylesheet.
 *
 * Deliberately narrow:
 * - Pointer must genuinely hover. `(hover: hover) and (pointer: fine)` keeps
 *   this away from touch devices, where a hover-open menu swallows the first
 *   tap and the item can never be reached.
 * - Desktop widths only. Below 990px Dawn switches to the drawer menu, which
 *   is a different snippet entirely.
 * - Click still toggles. Keyboard users, and anyone who clicks out of habit,
 *   get exactly the behaviour they had before.
 *
 * The close delay matters: without it, moving the pointer diagonally from the
 * summary towards the submenu crosses a few pixels of dead space and the menu
 * snaps shut before it can be reached.
 */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 990px)';
  var HOVER_CAPABLE = '(hover: hover) and (pointer: fine)';
  var CLOSE_DELAY = 180;

  function canHover() {
    return window.matchMedia(HOVER_CAPABLE).matches && window.matchMedia(DESKTOP).matches;
  }

  function setup() {
    var menus = document.querySelectorAll('.header__inline-menu header-menu');
    if (!menus.length) return;

    Array.prototype.forEach.call(menus, function (menu) {
      var details = menu.querySelector('details');
      if (!details) return;

      var summary = details.querySelector('summary');
      var closeTimer = null;

      function open() {
        if (!canHover()) return;
        window.clearTimeout(closeTimer);

        // One open at a time, matching what a click gives you.
        Array.prototype.forEach.call(menus, function (other) {
          var otherDetails = other.querySelector('details');
          if (otherDetails && otherDetails !== details) {
            otherDetails.removeAttribute('open');
            var otherSummary = otherDetails.querySelector('summary');
            if (otherSummary) otherSummary.setAttribute('aria-expanded', 'false');
          }
        });

        details.setAttribute('open', '');
        if (summary) summary.setAttribute('aria-expanded', 'true');
      }

      function scheduleClose() {
        if (!canHover()) return;
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(function () {
          details.removeAttribute('open');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        }, CLOSE_DELAY);
      }

      menu.addEventListener('mouseenter', open);
      menu.addEventListener('mouseleave', scheduleClose);

      // Cancel a pending close if the pointer comes back, including from the
      // submenu itself, which sits inside <details> and so inside <header-menu>.
      menu.addEventListener('mousemove', function () {
        if (details.hasAttribute('open')) window.clearTimeout(closeTimer);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      Array.prototype.forEach.call(menus, function (menu) {
        var details = menu.querySelector('details');
        if (details && details.hasAttribute('open')) {
          details.removeAttribute('open');
          var summary = details.querySelector('summary');
          if (summary) summary.setAttribute('aria-expanded', 'false');
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
